/**
 * TaskManagerModal.js
 *
 * Task Manager modal component for viewing, managing, and downloading workflow tasks.
 * Features:
 * - Display tasks from last 60 days
 * - Editable workflow names
 * - Expandable workflow config display (module details excluding prompts)
 * - Download functionality for completed tasks
 * - Pagination support
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Table, Badge, Button, Spinner, Alert,
    Form, InputGroup, Pagination, Collapse, Card
} from 'react-bootstrap';
import {
    FaTasks, FaDownload, FaEdit, FaCheck, FaTimes,
    FaChevronDown, FaChevronRight
} from 'react-icons/fa';
import { workflowAPI } from '../../utils/httpClient';
import { useI18n } from '../../i18n';

const ITEMS_PER_PAGE = 15;
const DAYS_FILTER = 60;

// Module type icons and colors
const MODULE_CONFIG = {
    fileInput: { icon: '📄', color: '#6c757d' },
    termBase: { icon: '📚', color: '#17a2b8' },
    memoryBase: { icon: '🧠', color: '#6f42c1' },
    translation: { icon: '🌐', color: '#28a745' },
    review: { icon: '✍️', color: '#fd7e14' },
    summary: { icon: '📋', color: '#20c997' }
};

// Fields to display for each module type (order matters)
const MODULE_DISPLAY_FIELDS = {
    fileInput: ['fileName', 'sourceLanguage', 'targetLanguage', 'sourceColumn', 'targetColumn'],
    termBase: ['cloudFile', 'targetColumns'],
    memoryBase: ['cloudFile', 'targetColumns'],
    translation: ['model', 'promptId', 'promptName', 'topP', 'maxTokens'],
    review: ['model', 'promptId', 'promptName', 'topP', 'maxTokens'],
    summary: ['model', 'temperature', 'topP', 'maxTokens']
};

const TaskManagerModal = ({ show, onHide }) => {
    const { t } = useI18n();

    // State
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    // Expanded rows state
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Editing state
    const [editingJobId, setEditingJobId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [savingName, setSavingName] = useState(false);

    // Download state
    const [downloadingJobId, setDownloadingJobId] = useState(null);

    // Toggle row expansion
    const toggleRowExpansion = (jobId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(jobId)) {
                newSet.delete(jobId);
            } else {
                newSet.add(jobId);
            }
            return newSet;
        });
    };

    // Fetch tasks
    const fetchTasks = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const offset = (page - 1) * ITEMS_PER_PAGE;
            const response = await workflowAPI.listWorkflowJobs({
                days: DAYS_FILTER,
                limit: ITEMS_PER_PAGE,
                offset: offset
            });
            if (response.success) {
                setTasks(response.data.jobs || []);
                setTotalCount(response.data.total_count || 0);
            } else {
                setError(response.message || t('taskManager:errors.fetchFailed'));
            }
        } catch (err) {
            console.error('Error fetching tasks:', err);
            setError(t('taskManager:errors.networkError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    // Fetch on modal open
    useEffect(() => {
        if (show) {
            setCurrentPage(1);
            setExpandedRows(new Set());
            fetchTasks(1);
        }
    }, [show, fetchTasks]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        setExpandedRows(new Set());
        fetchTasks(page);
    };

    // Start editing name
    const handleEditStart = (e, job) => {
        e.stopPropagation();
        setEditingJobId(job.job_id);
        setEditingName(job.workflow_name || '');
    };

    // Cancel editing
    const handleEditCancel = () => {
        setEditingJobId(null);
        setEditingName('');
    };

    // Save name
    const handleEditSave = async () => {
        if (!editingJobId || !editingName.trim()) return;

        setSavingName(true);
        try {
            const response = await workflowAPI.updateWorkflowName(editingJobId, editingName.trim());
            if (response.success) {
                setTasks(prev => prev.map(task =>
                    task.job_id === editingJobId
                        ? { ...task, workflow_name: editingName.trim() }
                        : task
                ));
                setEditingJobId(null);
                setEditingName('');
            } else {
                setError(response.message || t('taskManager:errors.updateFailed'));
            }
        } catch (err) {
            console.error('Error updating workflow name:', err);
            setError(t('taskManager:errors.updateFailed'));
        } finally {
            setSavingName(false);
        }
    };

    // Handle key press in edit input
    const handleEditKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleEditSave();
        } else if (e.key === 'Escape') {
            handleEditCancel();
        }
    };

    // Handle download
    const handleDownload = async (e, jobId) => {
        e.stopPropagation();
        setDownloadingJobId(jobId);
        try {
            const response = await workflowAPI.getWorkflowResults(jobId);
            if (response.success && response.data?.download_urls) {
                const urls = response.data.download_urls;
                const downloadUrl = urls.primary_download || urls.workflow_results_package;
                if (downloadUrl) {
                    window.open(downloadUrl, '_blank');
                } else {
                    setError(t('taskManager:errors.noDownloadUrl'));
                }
            } else {
                setError(t('taskManager:errors.downloadFailed'));
            }
        } catch (err) {
            console.error('Error downloading results:', err);
            setError(t('taskManager:errors.downloadFailed'));
        } finally {
            setDownloadingJobId(null);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusConfig = {
            SUCCESS: { bg: 'success', text: t('taskManager:status.success') },
            FAILED: { bg: 'danger', text: t('taskManager:status.failed') },
            STARTED: { bg: 'info', text: t('taskManager:status.processing') },
            PROCESSING: { bg: 'info', text: t('taskManager:status.processing') },
            PENDING: { bg: 'warning', text: t('taskManager:status.pending') },
            QUEUED: { bg: 'warning', text: t('taskManager:status.pending') }
        };
        const config = statusConfig[status] || { bg: 'secondary', text: status };
        return <Badge bg={config.bg}>{config.text}</Badge>;
    };

    // Render module chain as compact badges with names
    const renderModuleChain = (summary) => {
        if (!summary?.module_chain || summary.module_chain.length === 0) {
            return <span className="text-muted">-</span>;
        }

        return (
            <div className="d-flex flex-wrap align-items-center gap-1">
                {summary.module_chain.map((type, idx) => {
                    const config = MODULE_CONFIG[type] || { icon: '📦', color: '#6c757d' };
                    const moduleName = t(`taskManager:modules.${type}`) || type;
                    return (
                        <React.Fragment key={idx}>
                            <Badge
                                pill
                                bg=""
                                style={{
                                    backgroundColor: 'transparent',
                                    color: config.color,
                                    border: '1.5px solid #0d6efd',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    padding: '0.25rem 0.5rem'
                                }}
                            >
                                {config.icon} {moduleName}
                            </Badge>
                            {idx < summary.module_chain.length - 1 && (
                                <span className="text-muted" style={{ fontSize: '0.6rem' }}>→</span>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // Process config value based on field type
    const processConfigValue = (moduleType, key, rawConfig) => {
        const value = rawConfig[key];

        // Special handling for selectedPromptInfo
        if (key === 'promptId' || key === 'promptName') {
            const promptInfo = rawConfig.selectedPromptInfo;
            if (key === 'promptId') {
                return promptInfo?.id || t('taskManager:fields.noPromptId');
            }
            if (key === 'promptName') {
                return promptInfo?.name || t('taskManager:fields.noPromptName');
            }
        }

        // Special handling for model - only show model name
        if (key === 'model') {
            if (typeof value === 'object' && value !== null) {
                return value.name || value.id || String(value);
            }
            return value || '-';
        }

        // Special handling for cloudFile - only show file name
        if (key === 'cloudFile') {
            if (typeof value === 'object' && value !== null) {
                return value.name || value.fileName || value.file_name || String(value);
            }
            // Try to get from cloudFileName
            return rawConfig.cloudFileName || value || '-';
        }

        // Special handling for targetColumns - show full list
        if (key === 'targetColumns') {
            if (Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : '-';
            }
            return value || '-';
        }

        return value;
    };

    // Format display value
    const formatDisplayValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') {
            if (Number.isInteger(value)) return value.toString();
            return value.toFixed(2);
        }
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '-';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        const str = String(value);
        if (str.length > 60) return str.substring(0, 60) + '...';
        return str;
    };

    // Render expandable module details
    const renderModuleDetails = (summary) => {
        if (!summary?.module_details || summary.module_details.length === 0) {
            return <div className="text-muted small p-2">No configuration details available</div>;
        }

        return (
            <div className="d-flex flex-wrap gap-2 p-2" style={{ backgroundColor: '#f8f9fa' }}>
                {summary.module_details.map((module, idx) => {
                    const moduleType = module.type;
                    const config = MODULE_CONFIG[moduleType] || { icon: '📦', color: '#6c757d' };
                    const moduleName = t(`taskManager:modules.${moduleType}`) || moduleType;
                    const displayFields = MODULE_DISPLAY_FIELDS[moduleType] || [];
                    const rawConfig = module.config || {};

                    // Check if there are any displayable fields with values
                    const hasDisplayableContent = displayFields.some(field => {
                        if (field === 'promptId' || field === 'promptName') {
                            return rawConfig.selectedPromptInfo !== undefined;
                        }
                        return rawConfig[field] !== undefined && rawConfig[field] !== null && rawConfig[field] !== '';
                    });

                    if (!hasDisplayableContent && Object.keys(rawConfig).length === 0) return null;

                    return (
                        <Card
                            key={idx}
                            className="shadow-sm"
                            style={{
                                flex: '1 1 240px',
                                maxWidth: '320px',
                                minWidth: '220px',
                                borderLeft: `3px solid ${config.color}`,
                                borderRadius: '6px'
                            }}
                        >
                            <Card.Header
                                className="py-1 px-2 d-flex align-items-center"
                                style={{
                                    backgroundColor: `${config.color}08`,
                                    borderBottom: `1px solid ${config.color}15`
                                }}
                            >
                                <span style={{ fontSize: '0.9rem' }}>{config.icon}</span>
                                <span
                                    className="ms-2 fw-medium"
                                    style={{ fontSize: '0.75rem', color: config.color }}
                                >
                                    {moduleName}
                                </span>
                            </Card.Header>
                            <Card.Body className="py-2 px-2">
                                <div style={{ fontSize: '0.72rem' }}>
                                    {displayFields.map(fieldKey => {
                                        // Skip if no data for this field
                                        if (fieldKey === 'promptId' || fieldKey === 'promptName') {
                                            if (!rawConfig.selectedPromptInfo) return null;
                                        } else if (rawConfig[fieldKey] === undefined || rawConfig[fieldKey] === null || rawConfig[fieldKey] === '') {
                                            return null;
                                        }

                                        const value = processConfigValue(moduleType, fieldKey, rawConfig);
                                        const displayValue = formatDisplayValue(value);
                                        const fieldLabel = t(`taskManager:fields.${fieldKey}`) || fieldKey;

                                        return (
                                            <div
                                                key={fieldKey}
                                                className="d-flex justify-content-between align-items-start py-1"
                                                style={{ borderBottom: '1px solid #f0f0f0' }}
                                            >
                                                <span className="text-muted" style={{ minWidth: '70px', flexShrink: 0 }}>
                                                    {fieldLabel}
                                                </span>
                                                <span
                                                    className="text-end fw-medium ms-2"
                                                    style={{
                                                        wordBreak: 'break-word',
                                                        color: '#333'
                                                    }}
                                                    title={typeof value === 'string' ? value : JSON.stringify(value)}
                                                >
                                                    {displayValue}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}
            </div>
        );
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    // Render pagination
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
        );
        items.push(
            <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
        );

        if (startPage > 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        items.push(
            <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
        );
        items.push(
            <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        );

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                    {t('taskManager:pagination.showing', {
                        start: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                        end: Math.min(currentPage * ITEMS_PER_PAGE, totalCount),
                        total: totalCount
                    })}
                </small>
                <Pagination size="sm" className="mb-0">
                    {items}
                </Pagination>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaTasks className="me-2" />
                    {t('taskManager:title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {loading && (
                    <div className="text-center p-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">{t('taskManager:loading')}</p>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {!loading && tasks.length === 0 && (
                    <div className="text-center text-muted py-5">
                        <FaTasks size={48} className="mb-3" style={{ opacity: 0.3 }} />
                        <p>{t('taskManager:empty')}</p>
                    </div>
                )}

                {!loading && tasks.length > 0 && (
                    <>
                        <div className="table-responsive">
                            <Table hover size="sm" className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '28px' }}></th>
                                        <th style={{ minWidth: '150px' }}>{t('taskManager:columns.name')}</th>
                                        <th style={{ minWidth: '180px' }}>{t('taskManager:columns.workflow')}</th>
                                        <th className="text-center" style={{ width: '80px' }}>{t('taskManager:columns.status')}</th>
                                        <th className="text-center" style={{ width: '70px' }}>{t('taskManager:columns.cost')}</th>
                                        <th className="text-center d-none d-lg-table-cell" style={{ width: '130px' }}>{t('taskManager:columns.createdAt')}</th>
                                        <th className="text-center" style={{ width: '60px' }}>{t('taskManager:columns.action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tasks.map(task => {
                                        const isExpanded = expandedRows.has(task.job_id);
                                        const hasDetails = task.workflow_config_summary?.module_details?.length > 0;

                                        return (
                                            <React.Fragment key={task.job_id}>
                                                {/* Main Row */}
                                                <tr
                                                    onClick={() => hasDetails && toggleRowExpansion(task.job_id)}
                                                    style={{
                                                        cursor: hasDetails ? 'pointer' : 'default',
                                                        backgroundColor: isExpanded ? '#fafbfc' : 'transparent'
                                                    }}
                                                >
                                                    {/* Expand Toggle */}
                                                    <td className="text-center">
                                                        {hasDetails && (
                                                            <span style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                                                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* Workflow Name (Editable) */}
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {editingJobId === task.job_id ? (
                                                            <InputGroup size="sm">
                                                                <Form.Control
                                                                    type="text"
                                                                    value={editingName}
                                                                    onChange={(e) => setEditingName(e.target.value)}
                                                                    onKeyDown={handleEditKeyPress}
                                                                    onBlur={handleEditSave}
                                                                    autoFocus
                                                                    disabled={savingName}
                                                                    style={{ fontSize: '0.8rem' }}
                                                                />
                                                                <Button
                                                                    variant="outline-success"
                                                                    size="sm"
                                                                    onClick={handleEditSave}
                                                                    disabled={savingName}
                                                                >
                                                                    {savingName ? <Spinner size="sm" /> : <FaCheck />}
                                                                </Button>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    size="sm"
                                                                    onClick={handleEditCancel}
                                                                    disabled={savingName}
                                                                >
                                                                    <FaTimes />
                                                                </Button>
                                                            </InputGroup>
                                                        ) : (
                                                            <div
                                                                className="d-flex align-items-center"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={(e) => handleEditStart(e, task)}
                                                                title={t('taskManager:actions.clickToEdit')}
                                                            >
                                                                <FaEdit className="me-2 text-muted" size={10} />
                                                                <span
                                                                    className={task.workflow_name === 'Unnamed Workflow' ? 'text-muted fst-italic' : ''}
                                                                    style={{ fontSize: '0.8rem' }}
                                                                >
                                                                    {task.workflow_name || t('taskManager:unnamed')}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>

                                                    {/* Module Chain with Names */}
                                                    <td>
                                                        {renderModuleChain(task.workflow_config_summary)}
                                                    </td>

                                                    {/* Status */}
                                                    <td className="text-center">
                                                        {getStatusBadge(task.status)}
                                                        {task.test_mode && (
                                                            <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.6rem' }}>T</Badge>
                                                        )}
                                                    </td>

                                                    {/* Cost */}
                                                    <td className="text-center">
                                                        <span className="text-success fw-semibold" style={{ fontSize: '0.75rem' }}>
                                                            ${task.completion_cost?.toFixed(4) || '0.00'}
                                                        </span>
                                                    </td>

                                                    {/* Created At */}
                                                    <td className="text-center d-none d-lg-table-cell">
                                                        <small className="text-muted" style={{ fontSize: '0.72rem' }}>{formatDate(task.created_at)}</small>
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                        {task.status === 'SUCCESS' && task.has_download ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={(e) => handleDownload(e, task.job_id)}
                                                                disabled={downloadingJobId === task.job_id}
                                                                title={t('taskManager:actions.download')}
                                                                style={{ padding: '0.15rem 0.4rem' }}
                                                            >
                                                                {downloadingJobId === task.job_id ? (
                                                                    <Spinner animation="border" size="sm" />
                                                                ) : (
                                                                    <FaDownload size={11} />
                                                                )}
                                                            </Button>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* Expandable Details Row */}
                                                {hasDetails && (
                                                    <tr>
                                                        <td colSpan={7} className="p-0" style={{ border: 'none' }}>
                                                            <Collapse in={isExpanded}>
                                                                <div>
                                                                    {renderModuleDetails(task.workflow_config_summary)}
                                                                </div>
                                                            </Collapse>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>

                        {renderPagination()}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <small className="text-muted me-auto">
                    {t('taskManager:footer.showingDays', { days: DAYS_FILTER })}
                </small>
                <Button variant="secondary" onClick={onHide}>
                    {t('common:actions.close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TaskManagerModal;
